import { MagnifyingGlassIcon, PlusIcon } from '@phosphor-icons/react';
import { useCallback, useState } from 'react';
import { Link } from 'react-router-dom';

import { Input } from '../../../components/input/Input';
import { PageHeader } from '../../../components/PageHeader';
import { Pagination } from '../../../components/pagination/Pagination';
import { IPaginationVariables, usePagination } from '../../../hooks/usePagination';
import { useSearchParam } from '../../../hooks/useSearchParam';
import { LinkButton } from '../../../components/button/ButtonLink';
import { Table } from 'components/table/Table';
import { CheckboxWithLabel } from 'components/checkbox/CheckboxWithLabel';
import { listInternalUsers } from '../models';

const TABLE_HEADERS = [
  {
    id: 'name',
    name: 'Naam',
  },
  {
    id: 'email',
    name: 'E-mail',
  },
  {
    id: 'role',
    name: 'Rol',
  },
];

const TABLE_HEADERS_WITH_STATUS = [
  ...TABLE_HEADERS,
  {
    id: 'isActive',
    name: 'Is actief?',
  },
];

const UsersPage = () => {
  const [searchValue, setSearchValue] = useState('');
  const [showInactive, setShowInactive] = useSearchParam('showInactive', false);
  const pageFetcher = useCallback(
    async (variables: IPaginationVariables) => {
      const result = await listInternalUsers({
        cursor: variables.cursor ?? undefined,
        take: variables.take,
        search: searchValue,
        isActive: showInactive ? undefined : true,
      });
      return result.users;
    },
    [searchValue, showInactive],
  );
  const page = usePagination({
    key: 'id',
    pageSize: 20,
    initialCursor: undefined,
    fetcher: pageFetcher,
  });

  const handleSearchChange = (value: string) => {
    setSearchValue(value);
    page.reset();
  };

  const handleShowInactiveChange = (value: boolean) => {
    setShowInactive(value);
    page.reset();
  };

  return (
    <>
      <PageHeader title="Gebruikers" />

      <div className="grid gap-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className="heading-two">Gebruikers</h2>

          <div className="flex flex-wrap items-center gap-3">
            <CheckboxWithLabel
              labelText="Toon inactieve gebruikers"
              isChecked={showInactive}
              onChange={handleShowInactiveChange}
            />

            <div className="w-80">
              <Input
                type="text"
                placeholder="Zoek een gebruiker..."
                value={searchValue}
                onChange={handleSearchChange}
                iconLeft={<MagnifyingGlassIcon className="input-icon" />}
              />
            </div>

            <LinkButton to="new" color="primary" iconLeft={<PlusIcon className="button-icon" />}>
              Nieuwe gebruiker
            </LinkButton>
          </div>
        </div>

        <Table
          idKey="id"
          headers={showInactive ? TABLE_HEADERS_WITH_STATUS : TABLE_HEADERS}
          data={page.data}
          error={page.error}
          onRetry={page.retry}
          isLoading={page.isFetching}
          mapData={(user) => {
            const cells = [
              <Link to={user.id} className="link-text">
                {user.name}
              </Link>,
              user.email,
              user.role,
            ];
            return showInactive ? [...cells, user.isActive ? 'Actief' : 'Inactief'] : cells;
          }}
        />

        <div className="flex justify-end">
          <Pagination
            hasPrevious={page.hasPrevious}
            previous={page.previous}
            hasNext={page.hasNext}
            next={page.next}
            isFetching={page.isFetching}
          />
        </div>
      </div>
    </>
  );
};

export default UsersPage;
